import React from 'react';
import { Backdrop, Box, Typography, useTheme } from '@mui/material';
import { useAppSelector } from '../hooks/reduxHooks';

const orbitDot = (delay: number) => ({
  content: '""',
  position: 'absolute' as const,
  width: 10,
  height: 10,
  borderRadius: '50%',
  background: 'currentColor',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  animation: `orbit 1.6s linear ${delay}s infinite`,
});

const GlobalLoader: React.FC = () => {
  // Aggregate loading flags from slices; extend as needed
  const isLoading = useAppSelector((s) =>
    Boolean(
      (s as any).auth?.isLoading ||
      (s as any).users?.isLoading ||
      (s as any).entities?.isLoading ||
      (s as any).system?.isLoading ||
      (s as any).dataProcessing?.isLoading
    )
  );
  const theme = useTheme();

  return (
    <Backdrop
      open={isLoading}
      sx={{
        color: theme.palette.primary.main,
        zIndex: (t) => t.zIndex.modal + 1,
        backdropFilter: 'blur(4px)',
        backgroundColor:
          theme.palette.mode === 'dark'
            ? 'rgba(14,25,24,0.55)'
            : 'rgba(241,245,249,0.65)',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: { xs: 96, sm: 120, md: 140 },
          height: { xs: 96, sm: 120, md: 140 },
          color: theme.palette.primary.main,
          filter: `drop-shadow(0 8px 24px ${theme.palette.mode === 'dark' ? 'rgba(28,233,224,0.25)' : 'rgba(2,6,23,0.12)'})`,
          '::before': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: 36, sm: 44, md: 48 },
            height: { xs: 36, sm: 44, md: 48 },
            borderRadius: '50%',
            background: `linear-gradient(118deg, ${theme.palette.secondary.main}55, ${theme.palette.primary.main})`,
            boxShadow: `0 0 40px ${theme.palette.primary.main}55 inset`,
            animation: 'pulseCore 1.8s ease-in-out infinite',
          },
          '::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            border: `2px dashed ${theme.palette.secondary.main}88`,
            animation: 'spin 6s linear infinite',
          },
          '@keyframes pulseCore': {
            '0%, 100%': { transform: 'translate(-50%, -50%) scale(1)' },
            '50%': { transform: 'translate(-50%, -50%) scale(1.12)' },
          },
          '@keyframes spin': {
            '0%': { transform: 'rotate(0deg)' },
            '100%': { transform: 'rotate(360deg)' },
          },
          '@keyframes orbit': {
            '0%': { transform: 'translate(-50%, -50%) rotate(0deg) translateX(40px) rotate(0deg)' },
            '100%': { transform: 'translate(-50%, -50%) rotate(360deg) translateX(40px) rotate(-360deg)' },
          },
        }}
      >
        {/* orbital dots */}
        <Box sx={orbitDot(0)} />
        <Box sx={{ ...orbitDot(0.08), color: theme.palette.secondary.main }} />
        <Box sx={orbitDot(0.16)} />
        <Box sx={{ ...orbitDot(0.24), color: theme.palette.secondary.main }} />
        <Box sx={orbitDot(0.32)} />
        <Box sx={{ ...orbitDot(0.4), color: theme.palette.secondary.main }} />
      </Box>
      <Typography
        variant="subtitle2"
        sx={{ mt: 2, color: theme.palette.text.secondary, letterSpacing: 1, fontSize: { xs: 12, sm: 13 } }}
      >
        Initializing AI systems…
      </Typography>
    </Backdrop>
  );
};

export default GlobalLoader;


